import { Injectable } from '@angular/core';
import { Platform } from 'src/app/models/api';
import { ActionTypes } from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookAction, PlaybookStatus, PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import { PlaybookTemplatesDetailsModule } from 'src/app/models/templates/playbooks/playbookTemplateDetails.module';
import { PlaybookTemplateKey } from 'src/app/models/templates/playbooks/playbookTemplateKey.enum';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { ResourceStatus } from 'src/app/models/api/inventoryResource.model';
import { SendEmailActionDefinitionModel } from 'src/app/models/api/playbooks/actions/sendEmailProperties';
import { arePlatformActionsAllowed } from 'src/app/services/actions/common/actionsHelpers';
import { PlaybookCreationApiRequestModel } from 'src/app/models/api/playbooks/playbookCreationApiModel';
import { StoreService } from '../../infra/store/store.service';
import { Observable } from 'rxjs/internal/Observable';
import { IntegrationModel, StatusCodeEnum } from 'src/app/models/api/integration.model';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { emailTemplatesByTriggerType } from 'src/app/components/playbooks/playbook-action/actions/send-email-action/emailTemplates';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';

@Injectable({
  providedIn: 'root',
})
export class PlaybookTemplatesService {
  private _playbookTemplatesDetails = PlaybookTemplatesDetailsModule;
  private _mapTemplateIdToActionsAndTrigger: {
    [id in PlaybookTemplateKey]: {
      trigger: PlaybookTrigger;
      actions: PlaybookAction[];
    };
  };
  private _resourceTypeOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  constructor(
    private _labelsProviderPipe: LabelsProviderPipe,
    private _store: StoreService,
    private _policiesHttpService: PoliciesHttpService
  ) {
    this._generateTemplateIdToActionsAndTriggerMap();
  }

  /**
   * Generates a playbook creation object according to the values of a playbook template
   * Note: The function returns an observable since it calls integrations and policies values that might requirer a request to the server
   * @param templateId the template id to generate playbook from
   * @returns playbook creation object
   */
  public generatePlaybookFromTemplateId(templateId?: string): Observable<PlaybookCreationApiRequestModel> {
    const response = new Observable<PlaybookCreationApiRequestModel>((observer) => {
      const template = this._playbookTemplatesDetails.filter((t) => t.key == templateId)[0];

      if (!template) {
        observer.error('Template id not found');
      }

      this._selectIntegrationForPlatform(template.platform).subscribe((integrationId) => {
        if (integrationId) {
          this._selectPolicyForIntegration(integrationId).subscribe((policyId) => {
            const playbook: PlaybookCreationApiRequestModel = {
              name: this._labelsProviderPipe.transform(template.titleLabel),
              description: this._labelsProviderPipe.transform(template.descriptionLabel),
              platform: template.platform,
              status: PlaybookStatus.disabled,
              integrationId: integrationId ? integrationId : '',
              actions: this._mapTemplateIdToActionsAndTrigger[template.key].actions,
              trigger: this._mapTemplateIdToActionsAndTrigger[template.key].trigger,
              policyIds: policyId ? [policyId] : [],
            };

            observer.next(playbook);
          });
        } else {
          const playbook: PlaybookCreationApiRequestModel = {
            name: this._labelsProviderPipe.transform(template.titleLabel),
            description: this._labelsProviderPipe.transform(template.descriptionLabel),
            platform: template.platform,
            status: PlaybookStatus.disabled,
            integrationId: integrationId ? integrationId : '',
            actions: this._mapTemplateIdToActionsAndTrigger[template.key].actions,
            trigger: this._mapTemplateIdToActionsAndTrigger[template.key].trigger,
            policyIds: [],
          };
          observer.next(playbook);
        }
      });
    });
    return response;
  }

  public isIntegrationSupportedForPlaybookTemplates(integration: IntegrationModel): boolean {
    return (
      integration.connectionStatus.code == StatusCodeEnum.on &&
      arePlatformActionsAllowed(integration.platform, integration.authenticationMethod)
    );
  }

  public setResourceTypeOptions(resourceTypeOptions: ISelectOption[]): void {
    this._resourceTypeOptions$.next(resourceTypeOptions);
  }

  public getResourceTypeOptions$(): BehaviorSubject<ISelectOption[]> {
    return this._resourceTypeOptions$;
  }

  private _selectIntegrationForPlatform(platform: Platform): Observable<string> {
    const response = new Observable<string>((observer) => {
      this._store.integrations$.subscribe((integrations) => {
        const filteredIntegrations = integrations.filter(
          (integration) =>
            integration.platform == platform && this.isIntegrationSupportedForPlaybookTemplates(integration)
        );
        if (filteredIntegrations.length == 1) {
          observer.next(filteredIntegrations[0].id);
        } else {
          observer.next(undefined);
        }
      });
    });
    return response;
  }

  private _selectPolicyForIntegration(integrationId: string): Observable<string | undefined> {
    const response = new Observable<string | undefined>((observer) => {
      this._policiesHttpService.getIntegrationPolicies(integrationId).subscribe((policies) => {
        if (policies.length == 1) {
          observer.next(policies[0].id);
        } else {
          observer.next(undefined);
        }
      });
    });
    return response;
  }

  private _getSendEmailActionProperties(platform: Platform, contactMaker = false): SendEmailActionDefinitionModel {
    return {
      contactMaker: contactMaker,
      recipients: [],
      subject: emailTemplatesByTriggerType[TriggerTypes.newViolationFound].subject,
      bodyText: emailTemplatesByTriggerType[TriggerTypes.newViolationFound].body(platform),
    };
  }

  private _getNewViolationTriggerConfigurationForActiveResource(
    ruleIds: string[]
  ): NewViolationFoundTriggerConfigModel {
    return {
      ruleUpdatesEnabled: false,
      resourceStatus: [ResourceStatus.active],
      ruleIds: ruleIds,
    };
  }

  private _generateTemplateIdToActionsAndTriggerMap(): void {
    this._mapTemplateIdToActionsAndTrigger = {
      powerplatformInsecureCommunication: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource(['ZN_P00032']),
        },
        actions: [
          {
            actionType: ActionTypes.deleteConnection,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      powerplatformPersonalDataAccess: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource(['ZN_P00105']),
        },
        actions: [
          {
            actionType: ActionTypes.deleteConnection,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      powerplatformImplicitSharing: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource(['ZN_P00102']),
        },
        actions: [
          {
            actionType: ActionTypes.deleteConnection,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      powerplatformSuspiciousDataFlow: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource([
            'ZN_P00014',
            'ZN_P00037',
            'ZN_P00013',
            'ZN_P00041',
            'ZN_P00036',
          ]),
        },
        actions: [
          {
            actionType: ActionTypes.stopFlow,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      powerplatformExternalUsersAccess: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource([
            'ZN_P00200',
            'ZN_P00201',
            'ZN_P00206',
            'ZN_P00207',
          ]),
        },
        actions: [
          {
            actionType: ActionTypes.deleteConnection,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      powerplatformCustomConnectorMissingAuthentication: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource(['ZN_P00080']),
        },
        actions: [
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      powerplatformAnonymousAgentTriggerAccess: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource(['ZN_P00184', 'ZN_P00145']),
        },
        actions: [
          {
            actionType: ActionTypes.stopFlow,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
        ],
      },
      workatoPersonalDataAccess: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource([
            'ZN_W00040',
            'ZN_W00008',
            'ZN_W00022',
            'ZN_W00018',
          ]),
        },
        actions: [
          {
            actionType: ActionTypes.stopAllRecipes,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.workato),
          },
        ],
      },
      workatoInsecureCommunication: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource(['ZN_W00005']),
        },
        actions: [
          {
            actionType: ActionTypes.stopAllRecipes,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.workato),
          },
        ],
      },
      workatoSuspiciousDataFlow: {
        trigger: {
          triggerType: TriggerTypes.newViolationFound,
          configuration: this._getNewViolationTriggerConfigurationForActiveResource([
            'ZN_W00046',
            'ZN_W00034',
            'ZN_W00033',
            'ZN_W00048',
            'ZN_W00047',
          ]),
        },
        actions: [
          {
            actionType: ActionTypes.stopRecipe,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.workato),
          },
        ],
      },
    };
  }
}
